import React, { useState, useEffect, useRef } from 'react';
import { WaitlistService } from '../../../services/waitlistService';
import { OFFICES, APPOINTMENT_TITLES } from '../../../constants/calendar';
import { AppointmentService } from '../../../services/appointmentService';
import { isLucieVUser } from '../../../utils/userUtils';
import { getCurrentUser } from '../../../utils/auth';   
import { formatDateTime, formatPhone, formatLongDate } from '../../../utils/dateUtils';
import './styles/WaitlistSidebar.css';

/**
 * Waitlist sidebar for Lucie users2  f00897
 * - Add customers to waitlistf00897
 * - Scan for open time slots
 * - Assign waitlist entries to available slots
 */
const WaitlistSidebar = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Customer search for adding to waitlist
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Open slots scanner
  const [selectedOffice, setSelectedOffice] = useState('');
  const [selectedStylist, setSelectedStylist] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  // Assignment
  const [selectedWaitlistEntry, setSelectedWaitlistEntry] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [appointmentType, setAppointmentType] = useState('');
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);

  // Reason modal for adding to waitlist
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [waitlistReason, setWaitlistReason] = useState('');

  // Editable times (15-min increments)
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');

  const currentUser = getCurrentUser();
  // Check if user is Lucie/Yael
 
  // Fetch waitlist on mount and when sidebar opens
  useEffect(() => {
    if (isOpen && isLucieVUser(user)) {
      fetchWaitlist();
    }
  }, [isOpen, isLucieVUser(user)]);

  // Debounced customer search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await WaitlistService.searchCustomers(searchTerm);
        setCustomerSuggestions(results || []);
      } catch (err) {
        console.error('Error searching customers:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const fetchWaitlist = async () => {
    setIsLoading(true);
    try {
      const entries = await WaitlistService.getWaitlist();
      setWaitlistEntries(entries || []);
    } catch (err) {
      console.error('Error fetching waitlist:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleAddToWaitlist = (customer) => {
    // Show reason modal instead of adding directly
    setPendingCustomer(customer);
    setWaitlistReason('');
    setShowReasonModal(true);
    setSearchTerm('');
    setCustomerSuggestions([]);
  };

  const handleConfirmAddToWaitlist = async () => {
    if (!pendingCustomer) return;
    try {
      await WaitlistService.addToWaitlist({
        CustomerID: pendingCustomer.ID,
        CustomerName: pendingCustomer.CustomerFullName || `${pendingCustomer.CustomerFirstName} ${pendingCustomer.CustomerLastName}`,
        CustomerPhone: pendingCustomer.CustomerPhone,
        AddedBy: currentUser || 'System',
        Reason: waitlistReason || null
      });
      setShowReasonModal(false);
      setPendingCustomer(null);
      setWaitlistReason('');
      fetchWaitlist();
    } catch (err) {
      alert(err.message || 'Failed to add to waitlist');
    }
  };

  const handleCancelAddToWaitlist = () => {
    setShowReasonModal(false);
    setPendingCustomer(null);
    setWaitlistReason('');
  };

  const handleRemoveFromWaitlist = async (entryId) => {
    if (!window.confirm('Remove this customer from the waitlist?')) return;

    try {
      await WaitlistService.removeFromWaitlist(entryId, currentUser || 'System');
      fetchWaitlist();
      if (selectedWaitlistEntry?.ID === entryId) {
        setSelectedWaitlistEntry(null);
      }
    } catch (err) {
      alert(err.message || 'Failed to remove from waitlist');
    }
  };

  const handleScanForSlots = async () => {
    if (!selectedStylist) {
      alert('Please select a stylist');
      return;
    }

    setIsScanning(true);
    setScanResults(null);
    try {
      const results = await WaitlistService.scanOpenSlots(selectedStylist.id, 7);
      setScanResults(results);
    } catch (err) {
      console.error('Error scanning for slots:', err);
      alert('Failed to scan for open slots');
    } finally {
      setIsScanning(false);
    }
  };

  const handleSelectWaitlistEntry = (entry) => {
    setSelectedWaitlistEntry(selectedWaitlistEntry?.ID === entry.ID ? null : entry);
  };

  const handleSelectSlot = (date, slot) => {
    const slotKey = `${date}-${slot.startTime}`;
    const currentKey = selectedSlot ? `${selectedSlot.date}-${selectedSlot.startTime}` : null;

    if (slotKey === currentKey) {
      setSelectedSlot(null);
    } else {
      setSelectedSlot({ ...slot, date });
    }
  };

  const handleOpenAssignModal = () => {
    if (!selectedWaitlistEntry || !selectedSlot) {
      alert('Please select a customer from the waitlist and an available time slot');
      return;
    }
    setAppointmentType('');
    // Initialize editable times from selected slot
    setEditedStartTime(selectedSlot.startTime);
    setEditedEndTime(calculateDefaultEndTime(selectedSlot.startTime, selectedSlot.endTime));
    setShowAssignModal(true);
  };

  const handleCreateAppointment = async () => {
    if (!appointmentType) {
      alert('Please select an appointment type');
      return;
    }

    setIsCreatingAppointment(true);
    try {
      // Parse date correctly to avoid timezone issues
      // selectedSlot.date is in format 'YYYY-MM-DD'
      const [year, month, day] = selectedSlot.date.split('-').map(Number);
      const [startHours, startMinutes] = editedStartTime.split(':').map(Number);
      const [endHours, endMinutes] = editedEndTime.split(':').map(Number);

      // Create dates using local timezone (month is 0-indexed)
      const startDate = new Date(year, month - 1, day, startHours, startMinutes, 0, 0);
      const endDate = new Date(year, month - 1, day, endHours, endMinutes, 0, 0);

      const appointmentData = {
        ClientName: selectedWaitlistEntry.CustomerName,
        PhoneNum: selectedWaitlistEntry.CustomerPhone || '',
        ClientID: selectedWaitlistEntry.CustomerID,
        Title: appointmentType,
        EventDesc: selectedWaitlistEntry.Reason
          ? `From Waitlist - ${selectedWaitlistEntry.Reason}`
          : 'From Waitlist',
        EventStart: startDate.toISOString(),
        EventEnd: endDate.toISOString(),
        MeetingWith: selectedStylist.name,
        MWuserID: selectedStylist.id,
        AppointmentStatus: 'Booked'
      };

      // Create appointment
      const result = await AppointmentService.createAppointment(appointmentData);

      // Mark waitlist entry as booked
      await WaitlistService.markAsBooked(
        selectedWaitlistEntry.ID,
        result.appointmentId || result.ID,
        user?.username || 'System'
      );

      // Reset state
      setShowAssignModal(false);
      setSelectedWaitlistEntry(null);
      setSelectedSlot(null);
      setAppointmentType('');

      // Refresh data
      fetchWaitlist();
      handleScanForSlots(); // Refresh slots

      alert('Appointment created successfully!');
    } catch (err) {
      console.error('Error creating appointment:', err);
      alert(err.message || 'Failed to create appointment');
    } finally {
      setIsCreatingAppointment(false);
    }
  };


  const formatSlotTime = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate time options in 15-minute increments within a range
  const generateTimeOptions = (minTime = '08:00', maxTime = '19:00') => {
    const options = [];
    const [minHour, minMin] = minTime.split(':').map(Number);
    const [maxHour, maxMin] = maxTime.split(':').map(Number);
    const minTotal = minHour * 60 + minMin;
    const maxTotal = maxHour * 60 + maxMin;

    for (let hour = 8; hour <= 19; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const totalMinutes = hour * 60 + min;
        if (totalMinutes >= minTotal && totalMinutes <= maxTotal) {
          const time24 = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
          options.push(time24);
        }
      }
    }
    return options;
  };

  // Get valid start time options (within slot range)
  const getStartTimeOptions = () => {
    if (!selectedSlot) return generateTimeOptions();
    return generateTimeOptions(selectedSlot.startTime, selectedSlot.endTime);
  };

  // Get valid end time options (after selected start, up to slot end)
  const getEndTimeOptions = () => {
    if (!selectedSlot || !editedStartTime) return generateTimeOptions();
    // End time must be after start time (add 15 min minimum)
    const [startHour, startMin] = editedStartTime.split(':').map(Number);
    const minEndMinutes = startHour * 60 + startMin + 15;
    const minEndHour = Math.floor(minEndMinutes / 60);
    const minEndMin = minEndMinutes % 60;
    const minEndTime = `${minEndHour.toString().padStart(2, '0')}:${minEndMin.toString().padStart(2, '0')}`;
    return generateTimeOptions(minEndTime, selectedSlot.endTime);
  };

  // Calculate default end time (1 hour after start, or slot end if shorter)
  const calculateDefaultEndTime = (startTime, slotEndTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = slotEndTime.split(':').map(Number);

    // Default: 1 hour after start
    let defaultEndHour = startHour + 1;
    let defaultEndMin = startMin;

    // Cap at slot end time
    const endTotal = endHour * 60 + endMin;
    const defaultEndTotal = defaultEndHour * 60 + defaultEndMin;

    if (defaultEndTotal > endTotal) {
      defaultEndHour = endHour;
      defaultEndMin = endMin;
    }

    return `${defaultEndHour.toString().padStart(2, '0')}:${defaultEndMin.toString().padStart(2, '0')}`;
  };

  // Get staff list based on selected office
  const getStaffList = () => {
    if (!selectedOffice) return [];
    return OFFICES[selectedOffice]?.staff || [];
  };

  // Don't render if not Lucie
  if (!user || !isLucieVUser(user)) return null;

  return (
    <>
      {/* Toggle button - bottom-left to not conflict with SMS sidebar */}
      <button
        className={`waitlist-sidebar-toggle ${waitlistEntries.length > 0 ? 'has-entries' : ''}`}
        onClick={handleToggle}
        title="Waitlist"
      >
        <i class="fa-solid fa-list fa-xl"></i>
        {waitlistEntries.length > 0 && (
          <span className="entry-count">{waitlistEntries.length}</span>
        )}
      </button>

      {/* Sidebar panel */}
      <div className={`waitlist-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>Waitlist</h3>
          <button className="close-btn" onClick={handleToggle}>×</button>
        </div>

        <div className="sidebar-content">
          {/* Add to Waitlist Section */}
          <div className="waitlist-section">
            <h4 className="section-header">Add to Waitlist</h4>
            <div className="search-container">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search customer by name or phone..."
                className="customer-search-input"
              />
              {isSearching && <span className="search-spinner">...</span>}

              {customerSuggestions.length > 0 && (
                <div className="customer-suggestions">
                  {customerSuggestions.map((customer) => (
                    <div
                      key={customer.ID}
                      className="customer-suggestion-item"
                      onClick={() => handleAddToWaitlist(customer)}
                    >
                      <span className="customer-name-wl">
                        {customer.CustomerFullName || `${customer.CustomerFirstName} ${customer.CustomerLastName}`}
                      </span>
                      <span className="customer-phone">{customer.CustomerPhone}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Waitlist Entries Section */}
          <div className="waitlist-section">
            <h4 className="section-header">
              Waiting ({waitlistEntries.length})
            </h4>
            {isLoading ? (
              <div className="loading-text">Loading...</div>
            ) : waitlistEntries.length === 0 ? (
              <div className="empty-text">No customers on waitlist</div>
            ) : (
              <div className="waitlist-entries">
                {waitlistEntries.map((entry) => (
                  <div
                    key={entry.ID}
                    className={`waitlist-entry ${selectedWaitlistEntry?.ID === entry.ID ? 'selected' : ''}`}
                    onClick={() => handleSelectWaitlistEntry(entry)}
                  >
                    <div className="entry-info">
                      <span 
                      className="entry-name">{entry.CustomerName} - {formatPhone(entry.CustomerPhone)}
                      </span>
                      {/* <span className="entry-phone">{formatPhone(entry.CustomerPhone)}</span> */}
                      <span className="entry-phone">Current Appointment: {entry.EventStart ? formatDateTime(entry.EventStart) : 'No Current Appointment'}</span>
                      <span className="entry-phone">Meeting With: {entry.MeetingWith || 'N/A'}</span>
                      {entry.Reason && <span className="entry-reason">{entry.Reason}</span>}
                      <span className="entry-date">Added: {formatDateTime(entry.AddedDate)}</span>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFromWaitlist(entry.ID);
                      }}
                      title="Remove from waitlist"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scan for Open Slots Section */}
          <div className="waitlist-section">
            <h4 className="section-header">Scan for Open Slots</h4>
            <div className="scan-controls">
              <select
                value={selectedOffice}
                onChange={(e) => {
                  setSelectedOffice(e.target.value);
                  setSelectedStylist(null);
                  setScanResults(null);
                }}
                className="office-select"
              >
                <option value="">Select Office</option>
                {Object.entries(OFFICES).map(([key, office]) => (
                  <option key={key} value={key}>{office.name}</option>
                ))}
              </select>

              <select
                value={selectedStylist?.id || ''}
                onChange={(e) => {
                  const staff = getStaffList().find(s => s.id === parseInt(e.target.value));
                  setSelectedStylist(staff || null);
                  setScanResults(null);
                }}
                className="stylist-select"
                disabled={!selectedOffice}
              >
                <option value="">Select Stylist</option>
                {getStaffList().map((staff) => (
                  <option key={staff.id} value={staff.id}>{staff.name}</option>
                ))}
              </select>

              <button
                className="scan-btn"
                onClick={handleScanForSlots}
                disabled={!selectedStylist || isScanning}
              >
                {/* {isScanning ? 'Scanning...' : ''} */}
                <i className="fas fa-search search-icon"></i>
              </button>
            </div>

            {/* Scan Results */}
            {scanResults && (
              <div className="scan-results">
                {scanResults.slots?.length === 0 ? (
                  <div className="no-slots-text">No available slots found</div>
                ) : (
                  scanResults.slots?.map((day) => (
                    <div key={day.date} className="day-slots">
                      <div className="day-header">
                        {day.dayOfWeek}, {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="slots-list">
                        {day.availableSlots.map((slot, idx) => {
                          const isSelected = selectedSlot?.date === day.date &&
                                           selectedSlot?.startTime === slot.startTime;
                          return (
                            <div
                              key={idx}
                              className={`slot-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleSelectSlot(day.date, slot)}
                            >
                              <span className="slot-time">
                                {formatSlotTime(slot.startTime)} - {formatSlotTime(slot.endTime)}
                              </span>
                              <span className="slot-duration">{slot.durationMinutes} min</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Assign Button */}
          {(selectedWaitlistEntry || selectedSlot) && (
            <div className="assign-section">
              <div className="selection-summary">
                {selectedWaitlistEntry && (
                  <div className="selected-customer">
                    <strong>Customer:</strong> {selectedWaitlistEntry.CustomerName}
                  </div>
                )}
                {selectedSlot && selectedStylist && (
                  <div className="selected-slot">
                    <strong>Slot:</strong> {selectedSlot.date} at {formatSlotTime(selectedSlot.startTime)} with {selectedStylist.name}
                  </div>
                )}
              </div>
              <button
                className="assign-btn"
                onClick={handleOpenAssignModal}
                disabled={!selectedWaitlistEntry || !selectedSlot}
              >
                Assign to Slot
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {isOpen && <div className="waitlist-sidebar-overlay" onClick={handleToggle} />}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="assign-modal-overlay">
          <div className="assign-modal">
            <div className="modal-header">
              <h3>Create Appointment</h3>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>Customer:</strong> {selectedWaitlistEntry?.CustomerName}</p>
                <p><strong>Phone:</strong> {(selectedWaitlistEntry?.CustomerPhone || 'N/A')}</p>
                <p><strong>Date:</strong> {formatLongDate(selectedSlot?.date)}</p>
                <p><strong>Stylist:</strong> {selectedStylist?.name}</p>
                <p><strong>Description:</strong> {selectedWaitlistEntry?.Reason
                  ? `From Waitlist - ${selectedWaitlistEntry.Reason}`
                  : 'From Waitlist'}</p>
              </div>

              <div className="modal-form">
                <label htmlFor="appointmentType">Appointment Type *</label>
                <select
                  id="appointmentType"
                  value={appointmentType}
                  onChange={(e) => setAppointmentType(e.target.value)}
                  className="appointment-type-select"
                >
                  <option value="">Select Type</option>
                  {APPOINTMENT_TITLES.map((title) => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>

                <div className="time-edit-row">
                  <div className="time-edit-field">
                    <label htmlFor="startTime">Start Time</label>
                    <select
                      id="startTime"
                      value={editedStartTime}
                      onChange={(e) => setEditedStartTime(e.target.value)}
                      className="time-select"
                    >
                      {getStartTimeOptions().map((time) => (
                        <option key={time} value={time}>{formatSlotTime(time)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="time-edit-field">
                    <label htmlFor="endTime">End Time</label>
                    <select
                      id="endTime"
                      value={editedEndTime}
                      onChange={(e) => setEditedEndTime(e.target.value)}
                      className="time-select"
                    >
                      {getEndTimeOptions().map((time) => (
                        <option key={time} value={time}>{formatSlotTime(time)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
             
              <button
                className="create-btn"
                onClick={handleCreateAppointment}
                disabled={!appointmentType || isCreatingAppointment}
              >
                {isCreatingAppointment ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal for adding to waitlist */}
      {showReasonModal && pendingCustomer && (
        <div className="assign-modal-overlay">
          <div className="assign-modal reason-modal">
            <div className="modal-header">
              <h3>Add to Waitlist</h3>
              <button className="close-btn" onClick={handleCancelAddToWaitlist}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-info">
                <p><strong>Customer:</strong> {pendingCustomer.CustomerFullName || `${pendingCustomer.CustomerFirstName} ${pendingCustomer.CustomerLastName}`}</p>
                <p><strong>Phone:</strong> {pendingCustomer.CustomerPhone || 'N/A'}</p>
              </div>
              <div className="modal-form">
                <label htmlFor="waitlistReason">Reason for waiting</label>
                <textarea
                  id="waitlistReason"
                  value={waitlistReason}
                  onChange={(e) => setWaitlistReason(e.target.value)}
                  placeholder="Enter reason (e.g., waiting for cancellation, preferred time unavailable...)"
                  className="reason-textarea"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleCancelAddToWaitlist}>Cancel</button>
              <button className="create-btn" onClick={handleConfirmAddToWaitlist}>
                Add to Waitlist
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WaitlistSidebar;
